import type { GlobalThemeOverrides } from 'naive-ui'

// Brand tokens mirrored from the mobile app's design system
// (storypoint-mvp/src/theme/index.ts). The app is the source of truth for
// color values — update there first, then sync here.
//
// warning maps to the brand gold accent (not Naive's default orange) so
// admin/status tags carry the app's violet/gold identity.

export const fontFamily =
  "'Inter Variable', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif"

const shared: GlobalThemeOverrides['common'] = {
  fontFamily,
  fontSize: '16px',
  fontSizeMini: '13px',
  fontSizeTiny: '13px',
  fontSizeSmall: '15px',
  fontSizeMedium: '16px',
  fontSizeLarge: '17px',
  fontSizeHuge: '18px',
  heightMedium: '36px',
  heightLarge: '42px',
  // Naive's 3px default reads boxy; app radius tokens are sm=8 / md=12.
  borderRadius: '8px',
  borderRadiusSmall: '6px',
}

export const lightThemeOverrides: GlobalThemeOverrides = {
  common: {
    ...shared,
    bodyColor: '#F6F7F9',
    cardColor: '#FFFFFF',
    borderColor: '#E2E5EB',
    dividerColor: '#E2E5EB',
    primaryColor: '#5B21B6',
    primaryColorHover: '#6C33C4',
    primaryColorPressed: '#4A1B94',
    primaryColorSuppl: '#6C33C4',
    successColor: '#2E7D4F',
    successColorHover: '#38935F',
    successColorPressed: '#256540',
    errorColor: '#C0392B',
    errorColorHover: '#CF4B3D',
    errorColorPressed: '#A33024',
    warningColor: '#C9892E',
    warningColorHover: '#D69A44',
    warningColorPressed: '#B27825',
  },
  Card: {
    borderRadius: '12px',
  },
  // Key-value detail blocks render as quiet label-over-value pairs, not a
  // striped table: muted medium-weight label, value carries the weight.
  Descriptions: {
    thTextColor: '#5C5C5C',
    thFontWeight: '500',
  },
  // Checkable tags (the saved-view chips): the app's filter-chip language —
  // tonal neutral fill when off, solid brand violet when active. No border,
  // no bold: tonal fill carries the "tappable" affordance, the solid fill
  // carries the selection. Only checkable tags are affected; status tags
  // keep their own variants.
  Tag: {
    colorCheckable: '#ECEEF2',
    colorHoverCheckable: '#E2E5EB',
    colorPressedCheckable: '#D8DCE3',
    textColorCheckable: '#1A1A1A',
    colorChecked: '#5B21B6',
    colorCheckedHover: '#6C33C4',
    colorCheckedPressed: '#4A1B94',
    textColorChecked: '#FFFFFF',
  },
}

// Per-content-type accents: org overview cards and chart series that concern a
// specific content type use its hue consistently. One place to retune later.
// (Point CATEGORY colors are a separate system owned by the app —
// categoryIcons.gen.ts — pending the category rework.)
export const contentColors = {
  points: '#C0392B', // brick red
  trails: '#2E7D4F', // green
  viewpoints: '#3D6B8E', // fjord blue
  hunts: '#8A6FA8', // heather purple
  campaigns: '#A85D6F', // rosewood — story campaigns
  badges: '#C9892E', // brand gold
} as const

// Brand-adjacent categorical palette for charts — earthy, muted hues that sit
// with the violet/gold identity while staying distinguishable per series.
export const chartPalette = [
  '#5B21B6', // deep brand violet
  '#C9892E', // brand gold
  '#BCA0F5', // lavender (dark-theme primary)
  '#3D6B8E', // muted fjord blue
  '#C0392B', // brick
  '#8A6FA8', // muted heather
  '#E3A64F', // light gold
  '#6B7F72', // sage gray
]

export const darkThemeOverrides: GlobalThemeOverrides = {
  common: {
    ...shared,
    bodyColor: '#121417',
    cardColor: '#22282D',
    modalColor: '#22282D',
    popoverColor: '#22282D',
    borderColor: '#2C3137',
    dividerColor: '#2C3137',
    primaryColor: '#BCA0F5',
    primaryColorHover: '#CAB3F8',
    primaryColorPressed: '#A98AF0',
    primaryColorSuppl: '#CAB3F8',
    successColor: '#6BC795',
    successColorHover: '#82D2A6',
    successColorPressed: '#57B583',
    errorColor: '#E5736B',
    errorColorHover: '#EB8B84',
    errorColorPressed: '#DE5B52',
    warningColor: '#E3A64F',
    warningColorHover: '#E9B66C',
    warningColorPressed: '#DD963A',
  },
  Card: {
    borderRadius: '12px',
  },
  Descriptions: {
    thTextColor: '#9BA1A6',
    thFontWeight: '500',
  },
  // Mirrors the light theme's checkable-tag styling with the dark palette
  // (lavender primary needs dark text, same as the app's onPrimary).
  Tag: {
    colorCheckable: '#1C2024',
    colorHoverCheckable: '#242930',
    colorPressedCheckable: '#2C3137',
    textColorCheckable: '#F2F4F5',
    colorChecked: '#BCA0F5',
    colorCheckedHover: '#CAB3F8',
    colorCheckedPressed: '#A98AF0',
    textColorChecked: '#1C1130',
  },
}
